import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { PricePoint, AlertLevel } from '@flymind/shared';
import { findAirportByCode } from '@flymind/shared';
import { api, ApiClientError } from '../lib/api-client';
import { useTranslation } from '../hooks/useTranslation';

// The API returns Drizzle camelCase fields which differ from the shared types.
// Using a loose type to avoid mismatches until types are fully aligned.
type MonitorDetail = Record<string, any> & {
  id: string;
  aeroportoPartenza: string;
  aeroportoArrivo: string;
  dataAndata: string;
  dataRitorno?: string | null;
  soloAndata: boolean;
  budgetMax?: string | null;
  ultimoPrezzo?: string | null;
  mediaStorica?: string | null;
  statoAlert: string;
  attivo: boolean;
  notificheAttive: boolean;
  createdAt: string;
  priceHistory?: PricePoint[];
};
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { AlertBadge } from '../components/ui/AlertBadge';
import { Spinner } from '../components/ui/Spinner';
import { ShareModal } from '../components/ShareModal';

type PageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'not-found' }
  | { status: 'loaded'; data: MonitorDetail };

function formatDate(date: Date | string, locale: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const loc = locale === 'en' ? 'en-GB' : 'it-IT';
  return d.toLocaleDateString(loc, { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatShortDate(date: Date | string, locale: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const loc = locale === 'en' ? 'en-GB' : 'it-IT';
  return d.toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function MonitorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [shareOpen, setShareOpen] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [togglingNotif, setTogglingNotif] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [bookingOffers, setBookingOffers] = useState<any[]>([]);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSearched, setBookingSearched] = useState(false);
  const [lastPriceMeta, setLastPriceMeta] = useState<{
    compagnia: string | null;
    source: string | null;
    checkedAt: string | null;
  } | null>(null);

  const fetchMonitor = useCallback(async () => {
    if (!id) return;
    setState({ status: 'loading' });
    try {
      const data = await api.get<MonitorDetail>(`/monitoraggi/${id}`);
      setState({ status: 'loaded', data });
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) {
        setState({ status: 'not-found' });
      } else {
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : t('errors.generic'),
        });
      }
    }
  }, [id]);

  useEffect(() => {
    fetchMonitor();
  }, [fetchMonitor]);

  // Auto-refresh: dopo aver caricato il monitoraggio, lancia subito un
  // POST /:id/refresh che riesegue la ricerca live (TP→Duffel) e:
  //   - persiste il nuovo prezzo in priceHistory
  //   - aggiorna ultimo_prezzo, media_storica, stato_alert sul DB
  //   - ritorna le offerte live + i metadata dell'ultima entry priceHistory
  // L'utente vede il prezzo più recente possibile + le 3 offerte già pronte.
  // Una ricerca manuale fa override sulla sentinella (decisione di prodotto).
  const refreshLive = useCallback(async () => {
    if (!id) return;
    const today = new Date().toISOString().split('T')[0];
    setLoadingBooking(true);
    setBookingError('');
    try {
      const result = await api.post<{
        monitor: MonitorDetail;
        offers: any[];
        lastPriceMeta: { compagnia: string | null; source: string | null; checkedAt: string | null } | null;
      }>(`/monitoraggi/${id}/refresh`, {});
      setState({ status: 'loaded', data: result.monitor });
      setBookingOffers(result.offers ?? []);
      setLastPriceMeta(result.lastPriceMeta);
      setBookingSearched(true);
    } catch (err: unknown) {
      // Non bloccare la pagina su errore di refresh: il dato statico dal
      // GET resta visibile, mostriamo solo un messaggio inline sotto le offerte.
      const depDate = state.status === 'loaded' && typeof state.data.dataAndata === 'string'
        ? state.data.dataAndata
        : '';
      if (depDate && depDate < today) {
        setBookingError(t('monitorDetail.noBookingDate'));
      } else {
        setBookingError(err instanceof Error ? err.message : t('monitorDetail.searchOffersError'));
      }
      setBookingSearched(true);
    } finally {
      setLoadingBooking(false);
    }
  }, [id, t, state]);

  // Trigger del refresh dopo che il monitor è stato caricato la prima volta.
  // Si usa una flag locale per non rifare il refresh ad ogni cambio di state.
  const [refreshDone, setRefreshDone] = useState(false);
  useEffect(() => {
    if (state.status === 'loaded' && !refreshDone) {
      setRefreshDone(true);
      refreshLive();
    }
  }, [state.status, refreshDone, refreshLive]);

  async function handleToggle() {
    if (state.status !== 'loaded') return;
    setToggling(true);
    try {
      const updated = await api.patch<MonitorDetail>(`/monitoraggi/${id}`, {
        attivo: !state.data.attivo,
      });
      setState({ status: 'loaded', data: updated });
    } catch {
      // Silently fail - user can retry
    } finally {
      setToggling(false);
    }
  }

  async function handleToggleNotifiche() {
    if (state.status !== 'loaded') return;
    setTogglingNotif(true);
    try {
      const updated = await api.patch<MonitorDetail>(`/monitoraggi/${id}`, {
        notificheAttive: !state.data.notificheAttive,
      });
      setState({ status: 'loaded', data: updated });
    } catch {
      // Silently fail
    } finally {
      setTogglingNotif(false);
    }
  }

  async function handleDelete() {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/monitoraggi/${id}`);
      navigate('/dashboard', { replace: true });
    } catch {
      setDeleting(false);
      setDeleteConfirm(false);
    }
  }

  function handleBook(bookingUrl: string) {
    window.open(bookingUrl, '_blank', 'noopener');
  }

  // --- Loading state ---
  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  // --- Not found ---
  if (state.status === 'not-found') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="text-4xl mb-4">&#9992;&#65039;</div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">
          {t('monitorDetail.notFound')}
        </h2>
        <p className="text-text-secondary mb-6">
          {t('monitorDetail.notFoundDescription')}
        </p>
        <Button variant="primary" onClick={() => navigate('/dashboard')}>
          {t('monitorDetail.backToDashboard')}
        </Button>
      </div>
    );
  }

  // --- Error state ---
  if (state.status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="text-4xl mb-4">&#9888;&#65039;</div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">{t('common.error')}</h2>
        <p className="text-text-secondary mb-6">{state.message}</p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            {t('monitorDetail.backToDashboard')}
          </Button>
          <Button variant="primary" onClick={fetchMonitor}>
            {t('common.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  // --- Loaded ---
  const monitor = state.data;
  const depAirport = findAirportByCode(monitor.aeroportoPartenza);
  const arrAirport = findAirportByCode(monitor.aeroportoArrivo);
  const depName = depAirport?.city ?? monitor.aeroportoPartenza;
  const arrName = arrAirport?.city ?? monitor.aeroportoArrivo;
  const route = `${depName} \u2192 ${arrName}`;
  const currentPrice = monitor.ultimoPrezzo != null ? Number(monitor.ultimoPrezzo) : null;
  const avgPrice = monitor.mediaStorica != null ? Number(monitor.mediaStorica) : null;

  // Calculate percentage below/above average
  let percentDiff: number | null = null;
  let isBelow = false;
  if (currentPrice != null && avgPrice != null && avgPrice > 0) {
    percentDiff = Math.round(Math.abs(((currentPrice - avgPrice) / avgPrice) * 100));
    isBelow = currentPrice < avgPrice;
  }

  // AI recommendation (derived from available data)
  const hasAiRecommendation = currentPrice != null && avgPrice != null;
  let recommendationText = '';
  let trendText = '';
  let estimatedSavings: number | null = null;
  let confidenceLevel = 0;

  if (hasAiRecommendation && percentDiff != null) {
    if (isBelow) {
      recommendationText = `${t('monitorDetail.aiPriceIsAt')} ${percentDiff}% ${t('monitorDetail.aiBelowSuffix')}`;
      trendText = t('monitorDetail.aiTrendUp');
      estimatedSavings = Math.round(avgPrice! - currentPrice!);
      confidenceLevel = Math.min(95, 60 + percentDiff);
    } else {
      recommendationText = `${t('monitorDetail.aiPriceIsAt')} ${percentDiff}% ${t('monitorDetail.aiAboveSuffix')}`;
      trendText = t('monitorDetail.aiTrendDown');
      estimatedSavings = null;
      confidenceLevel = Math.max(30, 70 - percentDiff);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 pb-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors self-start cursor-pointer"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
          {t('monitorDetail.backToDashboard')}
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-text-primary truncate">
                {route}
              </h1>
              {monitor.statoAlert !== 'none' && <AlertBadge level={monitor.statoAlert as AlertLevel} />}
            </div>
            {!monitor.soloAndata && monitor.dataRitorno && (
              <p className="text-sm text-text-secondary mt-1">
                {formatShortDate(monitor.dataAndata, locale)} - {formatShortDate(monitor.dataRitorno, locale)}
              </p>
            )}
            {monitor.soloAndata && (
              <p className="text-sm text-text-secondary mt-1">
                {formatShortDate(monitor.dataAndata, locale)} &middot; {t('monitor.oneWay')}
              </p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/monitors?edit=${id}`)}
            >
              {t('common.edit')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              isLoading={deleting}
              onClick={handleDelete}
            >
              {deleteConfirm ? t('monitorDetail.confirmDeletion') : t('common.delete')}
            </Button>
          </div>
        </div>
      </div>

      {/* Miglior prezzo — card unificata: prezzo + meta + offerte live */}
      <Card variant="bordered" padding="lg">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-4">
          <div className="flex-1">
            {currentPrice != null ? (
              <>
                <p className="text-sm text-text-secondary mb-1">
                  {t('monitorDetail.bestPrice')}
                </p>
                <p className="text-4xl font-bold text-text-primary">
                  &euro;{Math.round(currentPrice * Math.max(1, Number(monitor.passeggeri ?? 1)))}
                  <span className="text-base font-normal text-text-secondary ml-2">{t('monitorDetail.total')}</span>
                </p>
                {Number(monitor.passeggeri ?? 1) > 1 && (
                  <p className="text-xs text-text-secondary mt-1">
                    {Number(monitor.passeggeri)} {t('monitorDetail.passengers')} &middot; &euro;{Math.round(currentPrice)} {t('monitorDetail.perPerson')}
                  </p>
                )}
                {percentDiff != null && (
                  <p className={`text-sm font-medium mt-1 ${isBelow ? 'text-green-600' : 'text-red-500'}`}>
                    {percentDiff}% {isBelow ? t('monitorDetail.belowAverage') : t('monitorDetail.aboveAverage')}
                    {avgPrice != null && (
                      <span className="ml-1 text-text-secondary font-normal">
                        (&euro;{Math.round(avgPrice)} {t('monitorDetail.last30Days')})
                      </span>
                    )}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-text-secondary mb-1">
                  {t('monitorDetail.bestPrice')}
                </p>
                <p className="text-2xl font-semibold text-text-secondary">
                  {t('monitorDetail.notYetAvailable')}
                </p>
              </>
            )}
          </div>

          {monitor.budgetMax != null && (
            <div className="text-sm text-text-secondary sm:text-right">
              <p>
                {t('monitorDetail.yourBudget')}:{' '}
                <span className={`font-medium ${currentPrice != null && currentPrice * Math.max(1, Number(monitor.passeggeri ?? 1)) <= Number(monitor.budgetMax) ? 'text-green-600' : 'text-text-primary'}`}>
                  &euro;{Math.round(Number(monitor.budgetMax))}
                </span>
                {currentPrice != null && currentPrice * Math.max(1, Number(monitor.passeggeri ?? 1)) <= Number(monitor.budgetMax) && (
                  <span className="text-green-600 ml-1">&#10003;</span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Meta consumer-friendly: compagnia, dove prenoti, freschezza prezzo */}
        {lastPriceMeta && (() => {
          const bookingSite = lastPriceMeta.source === 'duffel' ? 'Skyscanner' : 'Aviasales';
          const checkedAt = lastPriceMeta.checkedAt
            ? new Date(lastPriceMeta.checkedAt).toLocaleString(locale === 'en' ? 'en-GB' : 'it-IT', {
                day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
              })
            : null;
          return (
            <div className="text-sm text-text-secondary border-t border-border pt-3 mb-4">
              {lastPriceMeta.compagnia && (
                <p className="text-text-primary font-medium">
                  {t('monitorDetail.operatedBy')} {lastPriceMeta.compagnia}
                </p>
              )}
              <p className="mt-0.5">
                {t('monitorDetail.bookOn')} {bookingSite}
                {checkedAt && (
                  <>
                    {' · '}
                    {t('monitorDetail.updatedAt')} {checkedAt}
                  </>
                )}
              </p>
            </div>
          );
        })()}

        {/* CTA + offerte live */}
        {loadingBooking && (
          <div className="flex items-center justify-center py-4 gap-2 text-sm text-text-secondary">
            <Spinner size="sm" />
            <span>{t('monitorDetail.loadingFares')}</span>
          </div>
        )}

        {!loadingBooking && bookingError && (
          <div className="bg-red-50 rounded-lg p-3 text-sm text-red-700 mb-3">
            {bookingError}
          </div>
        )}

        {!loadingBooking && bookingOffers.length > 0 && (
          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-sm font-semibold text-text-primary">
              {t('monitorDetail.availableOffers')}
            </p>
            {bookingOffers.slice(0, 3).map((offer: any, idx: number) => {
              const oPax = Number(offer.passengers ?? 1);
              const oPerPax = Number(offer.perPax ?? 0);
              const oTotal = oPerPax * oPax;
              const durMin = Number(offer.duration ?? 0);
              const durLabel = durMin > 0
                ? `${Math.floor(durMin / 60)}h${(durMin % 60).toString().padStart(2, '0')}`
                : '';
              const carrierName: string = offer.airline || offer.airlineCode || t('monitorDetail.airline');
              const carrierCode: string | undefined = offer.airlineCode;
              const provider: string | undefined = offer.provider;
              const redirectTo = provider === 'travelpayouts' ? 'Aviasales' : provider === 'duffel' ? 'Skyscanner' : null;
              return (
                <div
                  key={offer.id ?? idx}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-border rounded-lg"
                >
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium text-text-primary">
                      {carrierName}
                      {carrierCode && carrierCode !== carrierName && (
                        <span className="ml-1 text-xs font-normal text-text-secondary">({carrierCode})</span>
                      )}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-text-secondary flex-wrap">
                      {oPerPax > 0 && (
                        <span className="text-lg font-bold text-text-primary">
                          &euro;{Math.round(oTotal)}
                          {oPax > 1 && (
                            <span className="text-xs font-normal text-text-secondary ml-1">
                              ({oPax} {t('monitorDetail.pax')} · &euro;{Math.round(oPerPax)}/{t('monitorDetail.pax')})
                            </span>
                          )}
                        </span>
                      )}
                      {offer.stops != null && (
                        <span>{offer.stops === 0 ? t('monitorDetail.direct') : `${offer.stops} ${offer.stops === 1 ? t('monitorDetail.stop') : t('monitorDetail.stops')}`}</span>
                      )}
                      {durLabel && <span>{durLabel}</span>}
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleBook(offer.bookingUrl)}
                  >
                    {t('monitorDetail.bookButton')} {redirectTo ?? t('monitorDetail.site')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {!loadingBooking && bookingSearched && bookingOffers.length === 0 && !bookingError && (
          <p className="text-sm text-text-secondary text-center py-2 border-t border-border pt-3">
            {t('monitorDetail.noFares')}
          </p>
        )}
      </Card>

      {/* AI Recommendation */}
      {hasAiRecommendation && (
        <Card variant="bordered" padding="md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <span className="text-lg" aria-hidden="true">&#129504;</span>
              <h2 className="text-base font-semibold text-text-primary">{t('monitorDetail.aiAnalysis')}</h2>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <p className="text-sm text-text-primary leading-relaxed">
              {recommendationText}
            </p>

            {/* Confidence */}
            <div>
              <div className="flex items-center justify-between text-xs text-text-secondary mb-1">
                <span>{t('monitorDetail.confidenceLevel')}</span>
                <span>{confidenceLevel}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-500 rounded-full h-2 transition-all"
                  style={{ width: `${confidenceLevel}%` }}
                />
              </div>
            </div>

            {/* Trend */}
            <div className="flex items-center gap-2 text-sm">
              <span
                className={`text-lg ${isBelow ? 'text-red-500' : 'text-green-600'}`}
                aria-hidden="true"
              >
                {isBelow ? '\u2191' : '\u2193'}
              </span>
              <span className="text-text-secondary">{trendText}</span>
            </div>

            {/* Estimated savings */}
            {estimatedSavings != null && estimatedSavings > 0 && (
              <div className="bg-green-50 rounded-lg p-3 text-sm">
                <span className="text-green-700 font-medium">
                  {t('monitorDetail.estimatedSavings')}: &euro;{estimatedSavings}
                </span>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          size="lg"
          className="flex-1 sm:flex-initial"
          onClick={() => setShareOpen(true)}
          iconLeft={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
          }
        >
          {t('monitorDetail.share')}
        </Button>
      </div>

      {/* Monitor details */}
      <Card padding="md">
        <CardHeader>
          <h2 className="text-base font-semibold text-text-primary">{t('monitorDetail.monitorDetails')}</h2>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div>
              <dt className="text-text-secondary">{t('monitor.departureAirport')}</dt>
              <dd className="font-medium text-text-primary mt-0.5">{depName} ({monitor.aeroportoPartenza})</dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t('monitor.arrivalAirport')}</dt>
              <dd className="font-medium text-text-primary mt-0.5">{arrName} ({monitor.aeroportoArrivo})</dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t('monitor.departureDate')}</dt>
              <dd className="font-medium text-text-primary mt-0.5">{formatDate(monitor.dataAndata, locale)}</dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t('monitor.returnDate')}</dt>
              <dd className="font-medium text-text-primary mt-0.5">
                {monitor.dataRitorno ? formatDate(monitor.dataRitorno, locale) : t('monitor.oneWay')}
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t('monitor.budget')}</dt>
              <dd className="font-medium text-text-primary mt-0.5">
                {monitor.budgetMax != null ? `\u20AC${Math.round(Number(monitor.budgetMax))}` : t('monitorDetail.notSet')}
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t('monitorDetail.createdOn')}</dt>
              <dd className="font-medium text-text-primary mt-0.5">{formatDate(monitor.createdAt, locale)}</dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t('monitorDetail.checksPerformed')}</dt>
              <dd className="font-medium text-text-primary mt-0.5">{(monitor.priceHistory ?? []).length}</dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t('common.status')}</dt>
              <dd className="mt-0.5">
                <button
                  onClick={handleToggle}
                  disabled={toggling}
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    monitor.attivo
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${monitor.attivo ? 'bg-green-500' : 'bg-gray-400'}`}
                  />
                  {toggling ? t('common.loading') : monitor.attivo ? t('common.active') : t('common.inactive')}
                </button>
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t('monitorDetail.notifications')}</dt>
              <dd className="mt-0.5">
                <button
                  onClick={handleToggleNotifiche}
                  disabled={togglingNotif}
                  title={t('monitorDetail.notificationsTooltip')}
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    monitor.notificheAttive
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${monitor.notificheAttive ? 'bg-blue-500' : 'bg-gray-400'}`}
                  />
                  {togglingNotif
                    ? t('common.loading')
                    : monitor.notificheAttive
                      ? t('monitorDetail.notificationsActive')
                      : t('monitorDetail.notificationsPaused')}
                </button>
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      {/* Share modal */}
      {currentPrice != null && (
        <ShareModal
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          route={route}
          price={currentPrice}
          percentBelow={isBelow && percentDiff != null ? percentDiff : null}
        />
      )}
    </div>
  );
}
